import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../../../../api';
import { API_BASE_URL } from '../constants/consts';
import { PolicyInsight, PolicyTemplate } from '../../../../types/policies';
import { universalMFAQueryKeys } from '../constants/queryKeys';

const END_POINT = '/insights';

type PolicyTemplateDto = Pick<
    PolicyTemplate,
    | 'id'
    | 'name'
    | 'typeId'
    | 'isFavorite'
    | 'description'
    | 'descriptionSummary'
    | 'disclaimer'
    | 'mfaNote'
    | 'editableFields'
    | 'supportedActions'
    | 'supportedProtocols'
    | 'authTypes'
    | 'spns'
    | 'risks'
> & {
    riskEnabled: boolean;
    riskIndicators: string[];
};

type PolicyInsightDto = Omit<PolicyInsight, 'template'> & {
    template: PolicyTemplateDto;
};

const fetchInsights = async (): Promise<PolicyInsightDto[]> => {
    const data = await apiGet<PolicyInsightDto[]>(`${API_BASE_URL}${END_POINT}`);
    return data;
};

const convertPolicyTemplateDtoToPolicyTemplate = (dto: PolicyTemplateDto): PolicyTemplate => {
    const { riskIndicators, riskEnabled, ...rest } = dto;
    return {
        ...rest,
        risk_indicators: riskIndicators,
        risk_enabled: riskEnabled
    };
};

const convertToPolicyInsight = (dto: PolicyInsightDto): PolicyInsight => {
    const { template, ...rest } = dto;

    return {
        ...rest,
        template: convertPolicyTemplateDtoToPolicyTemplate(template)
    };
};

const convertToPolicyInsights = (data: PolicyInsightDto[]): PolicyInsight[] => {
    return data.map(convertToPolicyInsight);
};

export const useInsights = (): UseQueryResult<PolicyInsight[]> => {
    return useQuery({
        queryKey: universalMFAQueryKeys.insights(),
        queryFn: fetchInsights,
        select: convertToPolicyInsights
    });
};
